/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren } from 'preact';
import type { Keys } from '#utils/UNSAFE_keys';
import type {
  CurrentItemVariant,
  ItemContext,
  ListItemRendererContext,
  SelectionDetail
} from '#UNSAFE_Collection';
import type { CollectionData } from '#PRIVATE_TableCommon/Common.types';
import type {
  ItemPadding,
  ListNoDataRendererContext,
  PromotedSection,
  SkeletonRendererContext
} from './List.types';
import type { ActiveCell, ViewportInfo } from './List';

import { useCallback } from 'preact/hooks';
import { globalImages } from '#ImageVars.css';
import { useTheme } from '#hooks/UNSAFE_useTheme';
import { classNames } from '#utils/UNSAFE_classNames';
import { containsKey } from '#utils/UNSAFE_keys';
import { LoadMoreCollection } from '#PRIVATE_LoadMoreCollection';
import { ReorderItemProps } from './hooks/useItemReorder';
import { sparkleStyles } from './themes/List.css';
import { Sparkle } from './Sparkle';
import { ListNoData } from './ListNoData';
import { MemoizeListRow } from './ListRow';
import { MemoizeListReorderableRow } from './ListReorderableRow';
import { ListSkeletonRow } from './ListSkeletonRow';

/**
 * Props for the ListBody Component
 */
type ListBodyProps<K extends string | number, D> = {
  viewportInfo: ViewportInfo;
  listId: string;
  listData: CollectionData<K, D>[];
  children: (context: ListItemRendererContext<K, D>) => ComponentChildren;
  itemPadding: ItemPadding;
  hasItemGridlines: boolean;
  hasTopGridline: boolean;
  hasBottomGridline: boolean;
  isShowFocusRing: boolean;
  isTabbableMode: boolean;
  hasMore: boolean;
  isRtl: boolean;
  isHighContrast: boolean;
  onLoadMore: () => void;
  activeCell?: ActiveCell<K>;
  currentItemKey?: K;
  currentItemVariant?: CurrentItemVariant;
  selectedKeys: Keys<K>;
  isSelectionEnabled: boolean;
  isSingleSelectionEnabled: boolean;
  onSelectionChange?: (detail: SelectionDetail<K>, isExtendableSelection: boolean) => void;
  promotedSection?: PromotedSection;
  noDataRenderer?: (context: ListNoDataRendererContext) => ComponentChildren;
  skeletonRenderer?: (context: SkeletonRendererContext) => ComponentChildren;
  disabledInteractionKeys?: ('enter' | 'space')[];
  reorderItemProps?: ReorderItemProps<K>;
};

/**
 * The internal component used to render the body of a List.
 */
export function ListBody<K extends string | number, D>({
  viewportInfo,
  listId,
  listData,
  children,
  itemPadding,
  hasItemGridlines,
  hasTopGridline,
  hasBottomGridline,
  isShowFocusRing,
  isTabbableMode,
  hasMore,
  onLoadMore,
  activeCell,
  currentItemKey,
  currentItemVariant,
  selectedKeys,
  isSelectionEnabled,
  isSingleSelectionEnabled,
  disabledInteractionKeys,
  onSelectionChange,
  promotedSection,
  noDataRenderer,
  skeletonRenderer,
  isRtl,
  isHighContrast,
  reorderItemProps
}: ListBodyProps<K, D>) {
  // returns a ListRowContext based on ItemContext (added key)
  const getRowContext = useCallback((context: ItemContext<CollectionData<K, D>>) => {
    return {
      index: context.index,
      data: context.data.data,
      key: context.data.metadata.key
    };
  }, []);

  let isPreviousSelected = false;
  let isPreviousSuggestion = false;
  const collectionChildrenFunc = (context: ItemContext<CollectionData<K, D>>) => {
    const listRowContext = getRowContext(context);
    const isSuggestion = promotedSection ? promotedSection.count > listRowContext.index : false;
    const rowKey = listRowContext.key;
    const containsActiveCell = activeCell?.rowKey === rowKey;
    const isSelected = containsKey(selectedKeys, rowKey);
    const isFinalRow = listRowContext.index === listData.length - 1;
    const isTopGridlineVisible =
      listRowContext.index === 0
        ? hasTopGridline
        : hasItemGridlines || (!isSuggestion && isPreviousSuggestion);
    const rowProps = {
      listId,
      children,
      padding: itemPadding,
      isSuggestion,
      hasTopGridline: isTopGridlineVisible,
      hasBottomGridline: isFinalRow && (hasMore ? hasItemGridlines : hasBottomGridline),
      isSelected,
      isPreviousSelected,
      disabledInteractionKeys,
      onSelectionChange,
      isSelectionEnabled,
      isSingleSelectionEnabled,
      variant: currentItemKey === rowKey ? currentItemVariant : 'none',
      isShowFocusRing: isShowFocusRing && containsActiveCell,
      isActive: containsActiveCell,
      isTabbableMode,
      selectedRowKeys: selectedKeys,
      rowIndex: listRowContext.index,
      rowKey,
      rowData: listRowContext.data,
      isFinalRow,
      isRtl,
      isHighContrast
    };
    isPreviousSelected = isSelected;
    isPreviousSuggestion = isSuggestion;
    if (reorderItemProps != null) {
      return <MemoizeListReorderableRow {...{ ...rowProps, ...reorderItemProps }} />;
    }
    return <MemoizeListRow {...rowProps} />;
  };

  const getNoDataProps = () => {
    const isActive = activeCell?.type === 'noData';
    const hasTabIndex = isActive && !isTabbableMode;
    return {
      listId: listId,
      isShowFocusRing: isShowFocusRing && isActive,
      hasTabIndex: hasTabIndex,
      isTabbableMode: isTabbableMode,
      ariaRowIndex: 1,
      renderer: noDataRenderer
    };
  };

  const isShowEmptyLoading = listData.length === 0 && hasMore;
  const isShowNoData = listData.length === 0 && !hasMore;

  const defaultLoadingIndicator = (
    <ListSkeletonRow
      listId={listId}
      hasTabIndex={
        isShowEmptyLoading ? activeCell?.type === 'loading' : activeCell?.type === 'loadMore'
      }
      isLoadMore={!isShowEmptyLoading}
      isShowFocusRing={
        isShowFocusRing &&
        (isShowEmptyLoading ? activeCell?.type === 'loading' : activeCell?.type === 'loadMore')
      }
      isHighContrast={isHighContrast}
      skeletonRenderer={skeletonRenderer}
    />
  );

  const { name } = useTheme();
  const sparkleContainerClasses = classNames([
    sparkleStyles.container,
    // TODO: this component should create separate theme definitions to only show the image in redwood
    name === 'redwood' && globalImages
  ]);

  return isShowNoData ? (
    <ListNoData {...getNoDataProps()} />
  ) : (
    <LoadMoreCollection<CollectionData<K, D>>
      data={listData}
      loadMoreIndicator={defaultLoadingIndicator}
      suggestions={<Sparkle />}
      suggestionsContainerClasses={sparkleContainerClasses}
      suggestionCount={promotedSection?.count}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      viewportConfig={viewportInfo.viewportConfig}>
      {collectionChildrenFunc}
    </LoadMoreCollection>
  );
}
